import type { TFunction } from 'i18next'
import { sdk } from '@/lib/sdk'
import type { FeedbackSettings } from '@/types/feedback'
import { errorsOf } from './feedbackUtils'

/** The API's range and default (feedbackConfig.NOMINEES); the settings carry them too. */
export const NOMINEES = Object.freeze({ min: 1, max: 20, default: 5 })

/** The field as typed: a string, so an empty box is not 0. */
export interface FeedbackSpaceConfig {
  maxNominees: string
}

export type FeedbackSpaceErrors = { maxNominees?: string } | null

export const defaultFeedbackSpaceConfig = (): FeedbackSpaceConfig => ({ maxNominees: String(NOMINEES.default) })

export const feedbackSpaceConfigFrom = (settings: FeedbackSettings): FeedbackSpaceConfig | null =>
  (settings.canEdit ? { maxNominees: String(settings.maxNominees) } : null)

const parsed = (config?: FeedbackSpaceConfig | null): number | null => {
  const raw = String(config?.maxNominees ?? '').trim()
  if (!raw) return null
  const value = Number(raw)
  return Number.isInteger(value) ? value : NaN
}

/** Empty is valid (back to the default); anything else is a whole number from 1 to 20. */
export const validateFeedbackSpaceConfig = (config: FeedbackSpaceConfig | null | undefined, t: TFunction): FeedbackSpaceErrors => {
  const value = parsed(config)
  if (value === null) return null
  if (Number.isNaN(value) || value < NOMINEES.min || value > NOMINEES.max) {
    return { maxNominees: t('feedbacks:about.settings.range', { min: NOMINEES.min, max: NOMINEES.max }) }
  }
  return null
}

export const saveFeedbackSpaceConfig = (spaceId: string, config: FeedbackSpaceConfig | null | undefined) =>
  sdk.feedbacks.updateSettings({ spaceId, maxNominees: parsed(config) })

export const feedbackSpaceErrorsFromApi = (err: unknown): FeedbackSpaceErrors => {
  const hit = errorsOf(err).find((e) => e.param === 'maxNominees')
  return hit ? { maxNominees: hit.message || '' } : null
}
